import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, GripVertical, Link, Upload } from "lucide-react";
import { toast } from "sonner";

interface HeroSlide {
  id: string;
  image_url: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export function HeroSlidesSection() {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);

  const fetchSlides = async () => {
    const { data, error } = await supabase
      .from("hero_slides")
      .select("*")
      .order("sort_order");
    if (error) {
      toast.error("Error loading slides");
    } else {
      setSlides(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchSlides(); }, []);

  const addSlideByUrl = async () => {
    if (!urlInput.trim()) return;
    const maxOrder = slides.length > 0 ? Math.max(...slides.map(s => s.sort_order)) + 1 : 0;
    const { error } = await supabase.from("hero_slides").insert({ image_url: urlInput.trim(), sort_order: maxOrder });
    if (error) { toast.error("Error adding slide"); return; }
    toast.success("Slide added");
    setUrlInput("");
    setDialogOpen(false);
    fetchSlides();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop();
    const fileName = `${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from("hero-images").upload(fileName, file);
    if (uploadError) { toast.error("Upload failed"); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from("hero-images").getPublicUrl(fileName);
    const maxOrder = slides.length > 0 ? Math.max(...slides.map(s => s.sort_order)) + 1 : 0;
    const { error } = await supabase.from("hero_slides").insert({ image_url: urlData.publicUrl, sort_order: maxOrder });
    if (error) { toast.error("Error saving slide"); setUploading(false); return; }
    toast.success("Slide uploaded");
    setUploading(false);
    setDialogOpen(false);
    fetchSlides();
  };

  const toggleActive = async (id: string, current: boolean) => {
    await supabase.from("hero_slides").update({ is_active: !current }).eq("id", id);
    fetchSlides();
  };

  const updateOrder = async (id: string, newOrder: number) => {
    await supabase.from("hero_slides").update({ sort_order: newOrder }).eq("id", id);
    fetchSlides();
  };

  const deleteSlide = async (id: string) => {
    await supabase.from("hero_slides").delete().eq("id", id);
    toast.success("Slide deleted");
    fetchSlides();
  };

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground">Manage hero background images</p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" /> Add Slide</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Hero Slide</DialogTitle></DialogHeader>
            <Tabs defaultValue="upload">
              <TabsList className="w-full">
                <TabsTrigger value="upload" className="flex-1"><Upload className="h-4 w-4 mr-1" /> Upload File</TabsTrigger>
                <TabsTrigger value="url" className="flex-1"><Link className="h-4 w-4 mr-1" /> Paste URL</TabsTrigger>
              </TabsList>
              <TabsContent value="upload" className="space-y-4 pt-4">
                <Label>Choose an image file</Label>
                <Input type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                {uploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
              </TabsContent>
              <TabsContent value="url" className="space-y-4 pt-4">
                <Label>Image URL</Label>
                <Input placeholder="https://..." value={urlInput} onChange={e => setUrlInput(e.target.value)} />
                <Button onClick={addSlideByUrl} className="w-full">Add</Button>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      {slides.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">No slides yet. Add your first hero image!</p>
      ) : (
        <div className="grid gap-4">
          {slides.map((slide) => (
            <Card key={slide.id} className="flex items-center gap-4 p-4">
              <GripVertical className="h-5 w-5 text-muted-foreground shrink-0" />
              <img src={slide.image_url} alt="" className="h-20 w-32 object-cover rounded-md border" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate text-muted-foreground">{slide.image_url}</p>
                <div className="flex items-center gap-3 mt-2">
                  <Label className="text-xs">Order:</Label>
                  <Input
                    type="number"
                    className="w-20 h-8"
                    value={slide.sort_order}
                    onChange={e => updateOrder(slide.id, parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={slide.is_active} onCheckedChange={() => toggleActive(slide.id, slide.is_active)} />
                <Button variant="ghost" size="icon" onClick={() => deleteSlide(slide.id)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
